<script lang="ts">
  import "ress";
  import "./global.css";
  import { onMount, type SvelteComponent } from "svelte";
  import type { ConditionsFailedEvent, RouteDefinition, RouteLoadedEvent } from "svelte-spa-router";
  import Router, { location, push, querystring, replace } from "svelte-spa-router";
  import { QueryClientProvider } from "@tanstack/svelte-query";
  import { wrap } from "svelte-spa-router/wrap";
  import { get } from "svelte/store";
  import SideMenu from "./components/side-menu/SideMenu.svelte";
  import { track } from "./helpers/mixpanel";
  import { landingQueryKey } from "./helpers/landing";
  import { resetStore, store, termsAndConditionsCheckStatus } from "./helpers/store";
  import ContactUs from "./routes/ContactUs.svelte";
  import IntegrationOAuth from "./routes/IntegrationOAuth.svelte";
  import Integrations from "./routes/Integrations.svelte";
  import Login from "./routes/Login.svelte";
  import Mobile from "./routes/Mobile.svelte";
  import StockTake from "./routes/StockTake.svelte";
  import TermsAndConds, { lookupTsAndCsStatus } from "./routes/TermsAndConds.svelte";
  import Recipes from "./routes/Recipes.svelte";
  import UAParser from "ua-parser-js";
  import SpendReport from "./routes/SpendReport.svelte";
  import { __DEV__ } from "./helpers/constants";
  import { getRekkiEnvironment } from "./helpers/environment";
  import SubscriptionModal from "./components/modals/SubscriptionModal.svelte";
  import { queryClient } from "./helpers/query-client";
  import { getInitialSubscriptionModalRedirectForUserSession } from "./helpers/subscription";
  import PendingInvoices from "./routes/PendingInvoices.svelte";
  import Playground from "./routes/Playground.svelte";
  import Order from "./routes/Order.svelte";
  import InvoicesNew from "./routes/InvoicesNew.svelte";
  import GPTracker from "./routes/GPTracker.svelte";
  import Prices from "./routes/Prices.svelte";
  import { locale } from "./helpers/localise";

  // env
  const REKKI_ENV = getRekkiEnvironment();

  const parser = new UAParser(navigator.userAgent);

  function isLoggedIn() {
    return $store.tokenExpires !== null && $store.tokenExpires > Date.now();
  }

  function isNotLoggedIn() {
    return $store.tokenExpires === null || $store.tokenExpires <= Date.now();
  }

  function isDesktop() {
    const device = parser.getDevice();

    return device.type === undefined || !["wearable", "mobile", "tablet"].includes(device.type);
  }

  async function acceptedTermsAndConditions() {
    try {
      const status = ($termsAndConditionsCheckStatus = await lookupTsAndCsStatus());
      return !status.needs_to_agree;
    } catch (e) {
      if (
        e &&
        typeof e === "object" &&
        "response" in e &&
        e.response &&
        typeof e.response === "object" &&
        "status" in e.response &&
        e.response.status === 401
      ) {
        console.warn("user unauthorized, redirecting to login");

        $store.token = null;
        push("/");
        return false;
      }

      throw e;
    }
  }

  async function notAcceptedTermsAndConditions() {
    return !(await acceptedTermsAndConditions());
  }

  function userOk(route: typeof SvelteComponent<any>) {
    return wrap({
      component: route,
      conditions: [usernameMatch, isLoggedIn, isDesktop, acceptedTermsAndConditions],
      userData: {
        redirect: "/terms-and-conditions",
        mobileRedirect: "/mobile",
      },
    });
  }

  function userNeedsTCs(route: typeof SvelteComponent<any>) {
    return wrap({
      component: route,
      conditions: [isLoggedIn, isDesktop, notAcceptedTermsAndConditions],
      userData: { redirect: "/", mobileRedirect: "/mobile" },
    });
  }

  function entrypoint(route: typeof SvelteComponent<any>) {
    const userData: { redirect?: string; mobileRedirect?: string; replace?: string } = {
      redirect: "/spend-report",
      mobileRedirect: "/mobile",
    };

    const modalRedirect = getInitialSubscriptionModalRedirectForUserSession();
    if (modalRedirect) {
      userData.redirect = modalRedirect;
    }

    if (window.location.pathname === "/public/mass-upload") {
      userData.replace = "/public/mass-upload";
    }

    return wrap({
      component: route,
      conditions: [isNotLoggedIn, isDesktop],
      userData,
    });
  }

  function conditionsFailed(event: ConditionsFailedEvent) {
    if (!usernameMatch()) {
      track("force_logout_from_email_source", {});
      resetStore();
      $store.originalRoute = event.detail.route;
      window.location.href = "/" + window.location.search.replace(/username=[^&]+&?/, "");
      return;
    }

    // so that after login we go to the desired route
    if (isNotLoggedIn() && event.detail.route !== "/terms-and-conditions") {
      $store.originalRoute = event.detail.route;
    }

    const { userData = {} as any } = event.detail;

    if ("replace" in userData && get(location) !== userData["replace"]) {
      replace(userData["replace"]);
      return;
    }

    if (
      !isDesktop() &&
      "mobileRedirect" in userData &&
      get(location) !== userData["mobileRedirect"]
    ) {
      push(userData["mobileRedirect"]);
      return;
    }

    if ("redirect" in userData && get(location) !== userData["redirect"]) {
      push(userData["redirect"]);
    }
  }

  function handleRouteLoaded(e: RouteLoadedEvent) {
    trackRouteChange(e);
  }

  const routeEventNameByRoute: Record<string, string> = {
    "/stock-take": "stock_take_tab_click",
    "/prices": "price_history_tab_click",
    "/integrations": "integrations_tab_click",
    "/contact-us": "footer_contact_us_click",
    "/mobile": "mobile_landing_page_redirect",
  };

  function trackRouteChange(e: RouteLoadedEvent) {
    const event = routeEventNameByRoute[e.detail.location];
    if (!event) {
      return;
    }
    track(event, {});
  }

  function usernameMatch() {
    const urlParams = new URLSearchParams(window.location.search);

    if (!urlParams.has("username")) {
      return true;
    }

    return (
      urlParams.get("username") === $store.user?.username ||
      urlParams.get("username") === $store.user?.email
    );
  }

  const noNavRoutes: Record<string, boolean> = {
    "/public/mass-upload": true,
    "/mobile": true,
    "/terms-and-conditions": true,
  };

  const routes: RouteDefinition = {
    "/": entrypoint(Login),
    "/spend-report": userOk(SpendReport),
    "/report/review": userOk(PendingInvoices),
    "/report": userOk(InvoicesNew),
    "/recipes": userOk(Recipes),
    "/stock-take": userOk(StockTake),
    "/prices": userOk(Prices),
    "/integrations/:selected?": userOk(Integrations),
    "/setup": userOk(IntegrationOAuth),
    "/order/:id": userOk(Order),
    "/mobile": Mobile,
    "/terms-and-conditions": userNeedsTCs(TermsAndConds),
    "/contact-us": userOk(ContactUs),
    "/gp": userOk(GPTracker),
  };

  if (REKKI_ENV === "feat") {
    routes["/playground"] = userOk(Playground);
  }

  $: showSideMenu = () => {
    return isLoggedIn() && !noNavRoutes[$location];
  };

  async function setLocale() {
    const queryParams = new URLSearchParams($querystring);

    if (queryParams.has("locale")) {
      const newLocale = queryParams.get("locale")?.toLowerCase() as string;
      await locale.set(newLocale);

      // Reload if language has changed
      if (newLocale !== $locale) {
        window.location.reload();
      }
    }
  }

  onMount(async () => {
    // need to decopypasta, the functions that evaluate this aren't reactive because svelte doesn't
    // see the store dependencies at the callsites.
    // anyway, we need to fetch various data on landing; we do that in the process of logging in
    // when logging in but for landings that are already logged in we load it (early, its results
    // can cause ui changes) here.
    if ($store.tokenExpires !== null && $store.tokenExpires > Date.now()) {
      queryClient.invalidateQueries({ queryKey: landingQueryKey });
    }

    await setLocale();
  });
</script>

<QueryClientProvider client={queryClient}>
  <div class="main">
    {#if showSideMenu()}
      <SideMenu />
    {/if}

    <Router {routes} on:conditionsFailed={conditionsFailed} on:routeLoaded={handleRouteLoaded} />
    <SubscriptionModal />
  </div>
</QueryClientProvider>

<style>
  /* for consistent horizontal spacing in page header bars. */
  :global(.header-bar-spaced > *) {
    margin: 0px 15px;
  }

  .main {
    height: 100%;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-items: flex-start;
  }
</style>
